// The Copilot's state lives above the routes, so the conversation and the
// open drawer survive moving between pages (each page renders its own
// MainLayout). Pages tell it what is on screen with useCopilotFocus().
//
// The conversation is held in memory only — never localStorage — because it
// carries candidates' names and details. It is cleared on sign-out and when a
// different person signs in on the same tab.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CopilotContext = createContext(null);

const ownerNow = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u._id || u.id || u.email || '';
  } catch (e) {
    return '';
  }
};

export function CopilotProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [focus, setFocusState] = useState(null);      // { candidateId, candidateName, stageId, stageLabel }
  const [entries, setEntries] = useState([]);         // what the person sees
  const [transcript, setTranscript] = useState([]);   // what the server is sent back
  const [owner, setOwner] = useState(ownerNow());

  const reset = useCallback(() => {
    setEntries([]);
    setTranscript([]);
    setFocusState(null);
  }, []);

  // A different person on the same tab starts with an empty conversation.
  // Checked on every render on purpose: who is signed in lives in
  // localStorage, which React cannot subscribe to.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const now = ownerNow();
    if (now !== owner) {
      setOwner(now);
      reset();
      setOpen(false);
    }
  });

  const setFocus = useCallback((f) => setFocusState(f || null), []);

  const value = useMemo(() => ({
    open, setOpen, focus, setFocus, entries, setEntries, transcript, setTranscript, reset,
  }), [open, focus, setFocus, entries, transcript, reset]);

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export function useCopilot() {
  return useContext(CopilotContext);
}

/* A page says "this candidate / stage is in front of the person". Cleared
   when the page goes away. Safe to call outside the provider (does nothing). */
export function useCopilotFocus(focus) {
  const ctx = useContext(CopilotContext);
  const setFocus = ctx?.setFocus;
  const key = focus ? JSON.stringify(focus) : '';
  useEffect(() => {
    if (!setFocus) return undefined;
    setFocus(key ? JSON.parse(key) : null);
    return () => setFocus(null);
  }, [key, setFocus]);
}
